import { createFileRoute, Link } from "@tanstack/react-router";
import { buildSeo } from "@/lib/seo";
import { useState, useMemo } from "react";
import { Layout } from "@/components/Layout";
import { SEOHead } from "@/components/SEOHead";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from "@/components/ui/breadcrumb";
import { useAllGames, useResults } from "@/hooks/use-query-hooks";
import { GamePicker } from "@/components/GamePicker";
import { BarChart3, Loader2 } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";

type Search = { game?: string };

export const Route = createFileRoute("/tools/frequency-analyzer")({
  validateSearch: (s: Record<string, unknown>): Search => ({ game: typeof s.game === "string" ? s.game : undefined }),
  head: () => buildSeo({
    title: "Frequency Analyzer — Lottery Tools | USA Lottery Club",
    description: "Analyze number frequency for US lottery games. Discover hot, cold, and overdue numbers.",
    path: "/tools/frequency-analyzer",
  }),
  component: FrequencyAnalyzer,
});

function FrequencyAnalyzer() {
  const { game: initial } = Route.useSearch();
  const { data: games, isLoading } = useAllGames();
  const [selectedGame, setSelectedGame] = useState(initial || "");
  const [range, setRange] = useState("50");

  const game = games?.find((g) => g.slug === selectedGame);
  const { data: results } = useResults(game?.id, parseInt(range));

  const analysis = useMemo(() => {
    if (!results || results.length === 0) return null;
    const freq: Record<number, number> = {};
    results.forEach((r) => {
      (r.winning_numbers || []).forEach((n) => { freq[n] = (freq[n] || 0) + 1; });
    });
    const sorted = Object.entries(freq)
      .map(([n, c]) => ({ number: parseInt(n), count: c }))
      .sort((a, b) => b.count - a.count);
    return { hot: sorted.slice(0, 10), cold: [...sorted].reverse().slice(0, 10), chartData: sorted.slice(0, 20) };
  }, [results]);

  return (
    <Layout>
      <SEOHead title="Frequency Analyzer — Lottery Tools" description="Analyze number frequency for US lottery games." />
      <div className="container py-6 md:py-10 max-w-3xl">
        <Breadcrumb className="mb-4">
          <BreadcrumbList>
            <BreadcrumbItem><BreadcrumbLink asChild><Link to="/">Home</Link></BreadcrumbLink></BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem><BreadcrumbLink asChild><Link to="/tools">Tools</Link></BreadcrumbLink></BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem><BreadcrumbPage>Frequency Analyzer</BreadcrumbPage></BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
        <h1 className="font-display text-2xl md:text-3xl font-bold mb-6">
          <BarChart3 className="inline h-6 w-6 mr-2 text-primary" /> Lottery Number Frequency Analyzer
        </h1>
        {isLoading ? <Loader2 className="h-6 w-6 animate-spin text-primary" /> : (
          <Card className="mb-6">
            <CardContent className="pt-6 space-y-4">
              <GamePicker games={games} selectedSlug={selectedGame} onSelect={setSelectedGame} />
              <div>
                <Label>Draw Range</Label>
                <Select value={range} onValueChange={setRange}>
                  <SelectTrigger aria-label="Draw range"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="20">Last 20 draws</SelectItem>
                    <SelectItem value="50">Last 50 draws</SelectItem>
                    <SelectItem value="100">Last 100 draws</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>
        )}
        {analysis && (
          <div className="space-y-6">
            <Card>
              <CardHeader><CardTitle className="text-base">Top 20 Numbers by Frequency</CardTitle></CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={analysis.chartData}>
                      <XAxis dataKey="number" fontSize={12} />
                      <YAxis fontSize={12} />
                      <Tooltip />
                      <Bar dataKey="count" fill="hsl(220 70% 45%)" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
            <div className="grid gap-4 sm:grid-cols-2">
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-base">🔥 Most Frequent</CardTitle></CardHeader>
                <CardContent>
                  <div className="space-y-1">
                    {analysis.hot.map(({ number, count }) => (
                      <div key={number} className="flex items-center justify-between text-sm">
                        <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-accent text-accent-foreground font-bold text-xs">{number}</span>
                        <span className="text-muted-foreground">{count} times</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-base">❄️ Least Frequent</CardTitle></CardHeader>
                <CardContent>
                  <div className="space-y-1">
                    {analysis.cold.map(({ number, count }) => (
                      <div key={number} className="flex items-center justify-between text-sm">
                        <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-lottery-blue text-lottery-blue-foreground font-bold text-xs">{number}</span>
                        <span className="text-muted-foreground">{count} times</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}